/**
 * world_caps.ts — client-side mirror of the server Permission Matrix resolution
 * (`capForWorld`): the world OWNER holds every capability; otherwise the player's
 * `world_role` row resolves (caps JSON over the role preset); otherwise visitor
 * (enter-only).
 *
 * UI-GATING ONLY (Hard Rule #1): the server re-checks the capability on every
 * mutating reducer — this merely decides which build/script panels to show, so
 * "only worlds you may build in show the building UI".
 */
import { atlas } from '../state.js';

export interface WorldCaps {
  enter: boolean;
  build: boolean;
  decorate: boolean;
  script: boolean;
  take: boolean;
  admin: boolean;
}

const NONE: WorldCaps = { enter: false, build: false, decorate: false, script: false, take: false, admin: false };
const ALL: WorldCaps = { enter: true, build: true, decorate: true, script: true, take: true, admin: true };
const CAP_KEYS = Object.keys(NONE) as (keyof WorldCaps)[];

/** The server's role → caps presets (mirror of lib/permissions.ts). */
function presetFor(role: string): WorldCaps {
  switch (role) {
    case 'owner':
    case 'admin':
      return { ...ALL };
    case 'build':
      return { ...NONE, enter: true, build: true, decorate: true };
    case 'script':
      return { ...NONE, enter: true, script: true };
    case 'decorate':
      return { ...NONE, enter: true, decorate: true };
    default:
      return { ...NONE, enter: true };
  }
}

/** Resolve the LOCAL player's capabilities in `worldId` from replicated rows. */
export function capsIn(worldId: bigint | null): WorldCaps {
  const conn = atlas.conn;
  const me = atlas.identityHex;
  if (!conn || !me || worldId === null) return { ...NONE };
  const world = conn.db.world.worldId.find(worldId);
  if (world && world.owner.toHexString() === me) return { ...ALL };
  for (const r of conn.db.worldRole.iter()) {
    if (r.worldId !== worldId || r.identity.toHexString() !== me) continue;
    const base = presetFor(r.role);
    if (!r.caps) return base;
    try {
      const raw = JSON.parse(r.caps) as Partial<Record<keyof WorldCaps, unknown>>;
      for (const k of CAP_KEYS) if (typeof raw[k] === 'boolean') base[k] = raw[k] as boolean;
    } catch { /* malformed caps JSON → the role preset stands */ }
    return base;
  }
  return { ...NONE, enter: true };
}

/** Whether the local player may BUILD in `worldId` (owner, admin, or a build grant). */
export function canBuildIn(worldId: bigint | null): boolean {
  const c = capsIn(worldId);
  return c.build || c.admin;
}
